import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/shared/ui/input';

interface CrudToolbarProps {
  search: string;
  onSearchChange: (next: string) => void;
  /** Right-aligned action area (typically the "+ Nuevo" button). */
  primary_action?: React.ReactNode;
  /** Extra controls (filters, status pills) inserted between search and action. */
  filters?: React.ReactNode;
  placeholder_key?: string;
}

/**
 * Debounced search input + slot for filters/actions. Search debounces on the client
 * (300ms) before triggering the parent — avoids a round trip on every keystroke.
 */
export function CrudToolbar({
  search,
  onSearchChange,
  primary_action,
  filters,
  placeholder_key = 'crud.search_placeholder',
}: CrudToolbarProps): JSX.Element {
  const { t } = useTranslation();
  const [local, setLocal] = useState(search);

  useEffect(() => {
    setLocal(search);
  }, [search]);

  useEffect(() => {
    if (local === search) return;
    const handle = window.setTimeout(() => onSearchChange(local), 300);
    return () => window.clearTimeout(handle);
  }, [local, search, onSearchChange]);

  return (
    <>
      <div className="relative max-w-sm flex-1">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder={t(placeholder_key)}
          className="pl-9"
        />
      </div>
      {filters ? <div className="flex items-center gap-2">{filters}</div> : null}
      {primary_action ? <div className="ml-auto">{primary_action}</div> : null}
    </>
  );
}
